import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    UsersThree,
    Database,
    ListBullets,
    EnvelopeSimple,
    Shield,
    Trash,
    PaperPlaneTilt,
    Broom,
    ArrowsClockwise,
    CheckCircle,
    XCircle,
    UserPlus,
    Warning,
    Clock,
} from "@phosphor-icons/react";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["admin", "manager", "operator"];

export default function Admin() {
    const { isAdmin } = useAuth();

    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-white bg-grid flex items-center justify-center p-6">
                <div className="panel p-10 max-w-md w-full text-center" data-testid="admin-forbidden">
                    <Warning size={28} weight="bold" className="mx-auto mb-3 text-[#ff3333]" />
                    <h1 className="font-display text-2xl font-bold tracking-tight mb-2">
                        Admins only
                    </h1>
                    <p className="text-sm text-[#52525b]">
                        This page is restricted to admin accounts. Ask an existing admin to
                        promote your role from the Users tab.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white bg-grid" data-testid="admin-page">
            <main className="p-6 lg:p-8 max-w-[1280px] mx-auto w-full">
                <div className="mb-6">
                    <div className="label-kicker mb-2 flex items-center gap-2">
                        <Shield size={12} weight="bold" />
                        Admin Console
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                        Run the floor.
                    </h1>
                    <p className="mt-3 text-[#52525b] text-sm max-w-2xl">
                        Manage operator access, send the team digest, and audit every
                        sensitive action. Outbound to providers stays disabled — this
                        console is strictly for the team.
                    </p>
                </div>

                <Tabs defaultValue="users" className="w-full">
                    <TabsList className="mb-4" data-testid="admin-tabs">
                        <TabsTrigger value="users" data-testid="admin-tab-users">
                            <UsersThree size={13} weight="bold" /> Users
                        </TabsTrigger>
                        <TabsTrigger value="audit" data-testid="admin-tab-audit">
                            <ListBullets size={13} weight="bold" /> Audit Log
                        </TabsTrigger>
                        <TabsTrigger value="digest" data-testid="admin-tab-digest">
                            <EnvelopeSimple size={13} weight="bold" /> Digest Email
                        </TabsTrigger>
                        <TabsTrigger value="ops" data-testid="admin-tab-ops">
                            <Database size={13} weight="bold" /> Runs &amp; Cache
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="users"><UsersTab /></TabsContent>
                    <TabsContent value="audit"><AuditTab /></TabsContent>
                    <TabsContent value="digest"><DigestTab /></TabsContent>
                    <TabsContent value="ops"><OpsTab /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

// ==================== Users Tab ====================
function UsersTab() {
    const { email: meEmail } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [confirm, setConfirm] = useState(null); // {action, email, ...}
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ email: "", role: "operator", receives_digest: false });

    const reload = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const { data } = await axios.get(`${API}/admin/users`);
            setUsers(data.users || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const updateField = async (email, payload, optimisticPatch) => {
        // Optimistic update
        if (optimisticPatch) {
            setUsers((prev) => prev.map((u) => (u.email === email ? { ...u, ...optimisticPatch } : u)));
        }
        try {
            const { data } = await axios.patch(`${API}/admin/users/${encodeURIComponent(email)}`, payload);
            setUsers((prev) => prev.map((u) => (u.email === email ? { ...u, ...data } : u)));
            toast.success(`Updated ${email}`);
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message || "Update failed";
            toast.error(msg);
            reload();
        }
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        if (!form.email.trim() || !form.email.includes("@")) {
            toast.error("Enter a valid email");
            return;
        }
        setCreating(true);
        try {
            const { data } = await axios.post(`${API}/admin/users`, {
                email: form.email.trim().toLowerCase(),
                role: form.role,
                is_active: true,
                receives_digest: form.receives_digest,
            });
            setUsers((prev) => {
                const others = prev.filter((u) => u.email !== data.email);
                return [...others, data].sort((a, b) => a.email.localeCompare(b.email));
            });
            toast.success(`Added ${data.email}`);
            setForm({ email: "", role: "operator", receives_digest: false });
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message || "Create failed";
            toast.error(msg);
        } finally {
            setCreating(false);
        }
    };

    const performDelete = async (email) => {
        try {
            await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`);
            setUsers((prev) => prev.filter((u) => u.email !== email));
            toast.success(`Removed ${email}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Delete failed");
        }
    };

    const performDeactivate = async (email) => {
        await updateField(email, { is_active: false }, { is_active: false });
    };

    return (
        <section className="space-y-6" data-testid="admin-users-tab">
            <div className="panel" data-testid="admin-create-user">
                <div className="panel-header">
                    <span className="label-kicker flex items-center gap-2">
                        <UserPlus size={12} weight="bold" /> Add operator
                    </span>
                </div>
                <form onSubmit={submitCreate} className="p-5 grid grid-cols-1 md:grid-cols-[1fr_180px_auto_auto] gap-3 items-end">
                    <label className="flex flex-col gap-1">
                        <span className="label-kicker" style={{ fontSize: 9 }}>Email</span>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="border border-[#e4e4e7] px-3 py-2 mono text-[12px] focus:border-black focus:outline-none"
                            placeholder="user@team.example"
                            data-testid="admin-create-email"
                            required
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="label-kicker" style={{ fontSize: 9 }}>Role</span>
                        <select
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            className="border border-[#e4e4e7] px-3 py-2 mono text-[12px] focus:border-black focus:outline-none bg-white"
                            data-testid="admin-create-role"
                        >
                            {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
                        </select>
                    </label>
                    <label className="flex items-center gap-2 mono text-[12px]">
                        <Switch
                            checked={form.receives_digest}
                            onCheckedChange={(v) => setForm({ ...form, receives_digest: v })}
                            data-testid="admin-create-digest"
                        />
                        Digest
                    </label>
                    <button
                        type="submit"
                        disabled={creating}
                        className="btn-primary"
                        data-testid="admin-create-submit"
                    >
                        {creating ? "Adding…" : "Add"}
                    </button>
                </form>
            </div>

            <div className="panel" data-testid="admin-users-table">
                <div className="panel-header flex items-center justify-between">
                    <span className="label-kicker flex items-center gap-2">
                        <UsersThree size={12} weight="bold" /> Users ({users.length})
                    </span>
                    <button
                        onClick={reload}
                        className="btn-ghost mono text-[11px]"
                        data-testid="admin-users-refresh"
                    >
                        <ArrowsClockwise size={12} weight="bold" />
                        Refresh
                    </button>
                </div>
                {loading && <div className="p-8 text-center text-sm text-[#52525b]">Loading…</div>}
                {err && <div className="p-8 text-center text-sm text-[#ff3333]" data-testid="admin-users-error">{err}</div>}
                {!loading && !err && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-[#fafaf9] border-b border-[#e4e4e7] text-left">
                                    <th className="px-4 py-2 label-kicker">Email</th>
                                    <th className="px-4 py-2 label-kicker">Role</th>
                                    <th className="px-4 py-2 label-kicker">Active</th>
                                    <th className="px-4 py-2 label-kicker">Digest</th>
                                    <th className="px-4 py-2 label-kicker">Last Updated</th>
                                    <th className="px-4 py-2 label-kicker text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => {
                                    const isSelf = u.email === meEmail;
                                    return (
                                        <tr key={u.email} className="border-b border-[#e4e4e7] last:border-0" data-testid={`admin-user-row-${u.email}`}>
                                            <td className="px-4 py-3 mono text-[12px] flex items-center gap-2">
                                                {u.email}
                                                {isSelf && <span className="mono text-[9px] px-1.5 py-0.5 bg-black text-white">YOU</span>}
                                                {u.seeded_from_env && (
                                                    <span className="mono text-[9px] px-1.5 py-0.5 border border-[#e4e4e7] text-[#71717a]" title="Seeded from .env on first boot">
                                                        SEEDED
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <select
                                                    value={u.role}
                                                    onChange={(e) => {
                                                        const newRole = e.target.value;
                                                        if (isSelf && newRole !== "admin") {
                                                            toast.error("You can't demote your own admin account.");
                                                            return;
                                                        }
                                                        setConfirm({
                                                            action: "role",
                                                            email: u.email,
                                                            from: u.role,
                                                            to: newRole,
                                                            run: () => updateField(u.email, { role: newRole }, { role: newRole }),
                                                        });
                                                    }}
                                                    className="border border-[#e4e4e7] px-2 py-1 mono text-[11px] bg-white focus:border-black focus:outline-none"
                                                    data-testid={`admin-role-${u.email}`}
                                                >
                                                    {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
                                                </select>
                                            </td>
                                            <td className="px-4 py-3">
                                                <Switch
                                                    checked={!!u.is_active}
                                                    disabled={isSelf}
                                                    onCheckedChange={(v) => {
                                                        if (!v) {
                                                            setConfirm({
                                                                action: "deactivate",
                                                                email: u.email,
                                                                run: () => performDeactivate(u.email),
                                                            });
                                                        } else {
                                                            updateField(u.email, { is_active: true }, { is_active: true });
                                                        }
                                                    }}
                                                    data-testid={`admin-active-${u.email}`}
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <Switch
                                                    checked={!!u.receives_digest}
                                                    onCheckedChange={(v) => updateField(u.email, { receives_digest: v }, { receives_digest: v })}
                                                    data-testid={`admin-digest-${u.email}`}
                                                />
                                            </td>
                                            <td className="px-4 py-3 mono text-[11px] text-[#71717a]">
                                                {u.updated_at ? new Date(u.updated_at).toLocaleString() : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={() => setConfirm({
                                                        action: "delete",
                                                        email: u.email,
                                                        run: () => performDelete(u.email),
                                                    })}
                                                    disabled={isSelf}
                                                    className="btn-ghost mono text-[11px] disabled:opacity-30 disabled:cursor-not-allowed"
                                                    data-testid={`admin-delete-${u.email}`}
                                                    title={isSelf ? "Cannot delete yourself" : "Remove user"}
                                                >
                                                    <Trash size={12} weight="bold" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {users.length === 0 && (
                                    <tr><td colSpan={6} className="px-4 py-10 text-center text-[#71717a] text-sm">No users yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(o) => { if (!o) setConfirm(null); }}
                title={confirmTitle(confirm)}
                description={confirmDescription(confirm)}
                confirmLabel={confirmLabel(confirm)}
                onConfirm={async () => {
                    if (confirm?.run) await confirm.run();
                    setConfirm(null);
                }}
            />
        </section>
    );
}

function confirmTitle(c) {
    if (!c) return "";
    if (c.action === "role") return `Change role to ${c.to}?`;
    if (c.action === "deactivate") return `Disable ${c.email}?`;
    if (c.action === "delete") return `Remove ${c.email}?`;
    return "Confirm action";
}
function confirmDescription(c) {
    if (!c) return "";
    if (c.action === "role") return `${c.email} will be moved from "${c.from}" to "${c.to}". They will see this change at next page load.`;
    if (c.action === "deactivate") return `${c.email} will lose access immediately. You can re-enable them later from this list.`;
    if (c.action === "delete") return `${c.email} will be permanently removed from access_controls. They will be denied at sign-in until re-added.`;
    return "";
}
function confirmLabel(c) {
    if (!c) return "Confirm";
    if (c.action === "delete") return "Remove";
    if (c.action === "deactivate") return "Disable";
    if (c.action === "role") return "Change role";
    return "Confirm";
}

// ==================== Audit Tab ====================
function AuditTab() {
    const [rows, setRows] = useState([]);
    const [actions, setActions] = useState([]);
    const [filters, setFilters] = useState({ actor_email: "", action: "" });
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const params = {};
            if (filters.actor_email) params.actor_email = filters.actor_email;
            if (filters.action) params.action = filters.action;
            const { data } = await axios.get(`${API}/admin/audit`, { params });
            setRows(data.entries || []);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/admin/audit/actions`);
                setActions(data.actions || []);
            } catch (err) {
                // Action filter is a UX nicety — fall back to free-text only.
                console.warn("[admin] audit/actions load failed:", err?.response?.status || err.message);
            }
        })();
    }, []);

    useEffect(() => { reload(); }, [reload]);

    return (
        <section className="space-y-4" data-testid="admin-audit-tab">
            <div className="panel">
                <div className="panel-header flex items-center justify-between">
                    <span className="label-kicker flex items-center gap-2">
                        <ListBullets size={12} weight="bold" />
                        Audit Trail · {rows.length}
                    </span>
                    <button onClick={reload} className="btn-ghost mono text-[11px]" data-testid="admin-audit-refresh">
                        <ArrowsClockwise size={12} weight="bold" />
                        Refresh
                    </button>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3">
                    <input
                        placeholder="Filter by actor email"
                        value={filters.actor_email}
                        onChange={(e) => setFilters({ ...filters, actor_email: e.target.value })}
                        className="border border-[#e4e4e7] px-3 py-2 mono text-[12px] focus:border-black focus:outline-none"
                        data-testid="admin-audit-filter-actor"
                    />
                    <select
                        value={filters.action}
                        onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                        className="border border-[#e4e4e7] px-3 py-2 mono text-[12px] focus:border-black focus:outline-none bg-white"
                        data-testid="admin-audit-filter-action"
                    >
                        <option value="">All actions</option>
                        {actions.map((a) => (<option key={a} value={a}>{a}</option>))}
                    </select>
                </div>
                {loading && <div className="p-8 text-center text-sm text-[#52525b]">Loading…</div>}
                {err && <div className="p-8 text-center text-sm text-[#ff3333]" data-testid="admin-audit-error">{err}</div>}
                {!loading && !err && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-[#fafaf9] border-b border-[#e4e4e7] text-left">
                                    <th className="px-4 py-2 label-kicker">Timestamp</th>
                                    <th className="px-4 py-2 label-kicker">Actor</th>
                                    <th className="px-4 py-2 label-kicker">Action</th>
                                    <th className="px-4 py-2 label-kicker">Target</th>
                                    <th className="px-4 py-2 label-kicker">Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-b border-[#e4e4e7] last:border-0" data-testid={`admin-audit-row-${r.id}`}>
                                        <td className="px-4 py-2 mono text-[11px] text-[#71717a] whitespace-nowrap">
                                            {new Date(r.timestamp).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 mono text-[11px]">{r.actor_email || "—"}</td>
                                        <td className="px-4 py-2 mono text-[11px]">
                                            <span className="px-1.5 py-0.5 border border-[#e4e4e7]">{r.action}</span>
                                        </td>
                                        <td className="px-4 py-2 mono text-[11px] text-[#52525b]">{r.target || "—"}</td>
                                        <td className="px-4 py-2 mono text-[10px] text-[#52525b] max-w-[420px] truncate" title={JSON.stringify(r.details || {})}>
                                            {Object.keys(r.details || {}).length === 0 ? "—" : JSON.stringify(r.details)}
                                        </td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr><td colSpan={5} className="px-4 py-10 text-center text-[#71717a] text-sm">No audit entries.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </section>
    );
}

// ==================== Digest Tab ====================
function DigestTab() {
    const { email: meEmail } = useAuth();
    const [status, setStatus] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [testTo, setTestTo] = useState("");
    const [confirm, setConfirm] = useState(null);
    const [lastError, setLastError] = useState(null); // {kind, message, code, at, target}

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [s, r, h] = await Promise.all([
                axios.get(`${API}/digest/status`),
                axios.get(`${API}/digest/recipients`),
                axios.get(`${API}/digest/history`, { params: { limit: 20 } }),
            ]);
            setStatus(s.data);
            setRecipients(r.data.recipients || []);
            setHistory(h.data.history || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const captureError = (e, kind, target) => {
        const code = e?.response?.status || null;
        const message = e?.response?.data?.detail || e?.message || "Send failed";
        setLastError({
            kind,
            message: String(message),
            code,
            at: new Date().toISOString(),
            target: target || null,
        });
        toast.error(message);
    };

    const sendBroadcast = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/digest/send`, { cadence: "manual" });
            toast.success(`Sent to ${data.sent} recipients (${data.failed} failed)`);
            setLastError(null);
            reload();
        } catch (e) {
            captureError(e, "broadcast", null);
        } finally {
            setBusy(false);
        }
    };

    const sendTest = async () => {
        const target = testTo.trim().toLowerCase();
        if (!target || !target.includes("@")) {
            toast.error("Enter a valid email for the test send");
            setLastError({
                kind: "test",
                message: "Enter a valid email for the test send",
                code: null,
                at: new Date().toISOString(),
                target: testTo || null,
            });
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/digest/send`, { test_to: target, cadence: "test" });
            toast.success(`Test sent (${data.sent}/${(data.sent || 0) + (data.failed || 0)})`);
            setLastError(null);
            reload();
        } catch (e) {
            captureError(e, "test", target);
        } finally {
            setBusy(false);
        }
    };

    const sendTestToMe = () => {
        if (!meEmail) {
            toast.error("Could not detect your email — sign out and back in.");
            return;
        }
        setTestTo(meEmail);
        // Run on next tick after state propagates
        setTimeout(() => {
            const target = (meEmail || "").trim().toLowerCase();
            (async () => {
                setBusy(true);
                try {
                    const { data } = await axios.post(`${API}/digest/send`, { test_to: target, cadence: "test" });
                    toast.success(`Test sent to ${target}`);
                    setLastError(null);
                    reload();
                } catch (e) {
                    captureError(e, "test", target);
                } finally {
                    setBusy(false);
                }
            })();
        }, 0);
    };

    return (
        <section className="space-y-6" data-testid="admin-digest-tab">
            <div className="panel">
                <div className="panel-header">
                    <span className="label-kicker flex items-center gap-2">
                        <EnvelopeSimple size={12} weight="bold" />
                        Resend Status
                    </span>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <StatusTile
                        label="Resend API"
                        value={status?.configured ? "Configured" : "Not configured"}
                        ok={!!status?.configured}
                        testId="digest-status-configured"
                    />
                    <StatusTile
                        label="Sender"
                        value={status?.sender || "—"}
                        ok
                        mono
                        testId="digest-status-sender"
                    />
                    <StatusTile
                        label="Opted-in recipients"
                        value={String(status?.recipients_count ?? recipients.length ?? 0)}
                        ok={(status?.recipients_count || 0) > 0}
                        testId="digest-status-recipients"
                    />
                </div>
                {!status?.configured && (
                    <div className="px-5 pb-5 -mt-1" data-testid="digest-not-configured-banner">
                        <div className="border-2 border-[#ff3333] p-4 text-[12px]" style={{ background: "#fff7e0" }}>
                            <div className="flex items-start gap-2">
                                <Warning size={14} weight="fill" color="#ff3333" className="shrink-0 mt-[2px]" />
                                <div className="flex-1">
                                    <div className="font-bold text-[#1a1a1a] mb-1">
                                        Resend is not configured — send buttons are disabled.
                                    </div>
                                    <div className="mono text-[11px] text-[#52525b] leading-relaxed">
                                        Add <code className="px-1 bg-white border border-[#e4e4e7]">RESEND_API_KEY=re_...</code> to <code className="px-1 bg-white border border-[#e4e4e7]">/app/backend/.env</code>, then run{" "}
                                        <code className="px-1 bg-white border border-[#e4e4e7]">sudo supervisorctl restart backend</code>.
                                        Sender defaults to <code className="px-1 bg-white border border-[#e4e4e7]">digest@resend.dev</code> (Resend's sandbox; only delivers to the Resend account owner). For team-wide delivery, verify a domain in Resend and set{" "}
                                        <code className="px-1 bg-white border border-[#e4e4e7]">DIGEST_SENDER_EMAIL=digest@yourdomain.com</code>.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {lastError && (
                <div className="panel" data-testid="digest-last-error" style={{ borderColor: "#ff3333" }}>
                    <div
                        className="panel-header flex items-center justify-between"
                        style={{ background: "#ffecec", borderBottom: "1px solid #ff3333" }}
                    >
                        <span className="label-kicker flex items-center gap-2" style={{ color: "#ff3333" }}>
                            <Warning size={12} weight="fill" /> Last send failed
                        </span>
                        <button
                            onClick={() => setLastError(null)}
                            className="btn-ghost mono text-[11px]"
                            data-testid="digest-error-dismiss"
                        >
                            Dismiss
                        </button>
                    </div>
                    <div className="p-4 grid grid-cols-1 md:grid-cols-[120px_1fr] gap-2 mono text-[11px]">
                        <span className="text-[#71717a]">Kind</span>
                        <span className="font-bold">{lastError.kind}{lastError.target ? ` → ${lastError.target}` : ""}</span>
                        <span className="text-[#71717a]">HTTP</span>
                        <span className="font-bold">{lastError.code ?? "—"}</span>
                        <span className="text-[#71717a]">When</span>
                        <span>{new Date(lastError.at).toLocaleString()}</span>
                        <span className="text-[#71717a]">Detail</span>
                        <span className="text-[#ff3333]" data-testid="digest-error-detail">{lastError.message}</span>
                    </div>
                    {lastError.code === 503 && (
                        <div className="px-4 pb-4 mono text-[11px] text-[#52525b]">
                            <strong>Fix:</strong> Set <code className="px-1 bg-white border border-[#e4e4e7]">RESEND_API_KEY</code>{" "}
                            in <code className="px-1 bg-white border border-[#e4e4e7]">/app/backend/.env</code> and run{" "}
                            <code className="px-1 bg-white border border-[#e4e4e7]">sudo supervisorctl restart backend</code>.
                        </div>
                    )}
                </div>
            )}

            <div className="panel">
                <div className="panel-header">
                    <span className="label-kicker">Manual send</span>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="label-kicker mb-2" style={{ fontSize: 9 }}>
                            Send to all opted-in recipients
                        </div>
                        <div className="mono text-[11px] text-[#52525b] mb-3">
                            {recipients.length === 0
                                ? "No recipients yet — toggle Digest on for at least one user under Users."
                                : `${recipients.length} address${recipients.length === 1 ? "" : "es"}: ${recipients.slice(0, 3).join(", ")}${recipients.length > 3 ? ` +${recipients.length - 3} more` : ""}`}
                        </div>
                        <button
                            onClick={() => setConfirm({ kind: "broadcast", count: recipients.length, run: sendBroadcast })}
                            disabled={busy || !status?.configured || recipients.length === 0}
                            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="digest-send-broadcast"
                        >
                            <PaperPlaneTilt size={14} weight="bold" />
                            {busy ? "Sending…" : "Send digest now"}
                        </button>
                    </div>
                    <div>
                        <div className="label-kicker mb-2 flex items-center justify-between" style={{ fontSize: 9 }}>
                            <span>Test send</span>
                            <button
                                onClick={sendTestToMe}
                                disabled={busy || !status?.configured || !meEmail}
                                className="btn-ghost mono text-[10px] disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="digest-send-test-to-me"
                                title={meEmail ? `Send a test to ${meEmail}` : "No email detected"}
                            >
                                <PaperPlaneTilt size={10} weight="bold" />
                                Send to me
                            </button>
                        </div>
                        <div className="flex gap-2">
                            <input
                                type="email"
                                value={testTo}
                                onChange={(e) => setTestTo(e.target.value)}
                                placeholder="you@team.example"
                                className="flex-1 border border-[#e4e4e7] px-3 py-2 mono text-[12px] focus:border-black focus:outline-none"
                                data-testid="digest-test-to"
                            />
                            <button
                                onClick={sendTest}
                                disabled={busy || !status?.configured}
                                className="btn-ghost mono text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="digest-send-test"
                            >
                                <PaperPlaneTilt size={12} weight="bold" />
                                Send test
                            </button>
                        </div>
                        <div className="mono text-[11px] text-[#52525b] mt-2">
                            Receives the same HTML, subject prefixed with TEST.
                        </div>
                    </div>
                </div>
            </div>

            <div className="panel" data-testid="digest-history">
                <div className="panel-header flex items-center justify-between">
                    <span className="label-kicker">Send history</span>
                    <button onClick={reload} className="btn-ghost mono text-[11px]" data-testid="digest-history-refresh">
                        <ArrowsClockwise size={12} weight="bold" /> Refresh
                    </button>
                </div>
                {loading ? (
                    <div className="p-8 text-center text-sm text-[#52525b]">Loading…</div>
                ) : history.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#71717a]">No sends yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-[#fafaf9] border-b border-[#e4e4e7] text-left">
                                    <th className="px-4 py-2 label-kicker">When</th>
                                    <th className="px-4 py-2 label-kicker">Actor</th>
                                    <th className="px-4 py-2 label-kicker">Cadence</th>
                                    <th className="px-4 py-2 label-kicker">Sent</th>
                                    <th className="px-4 py-2 label-kicker">Failed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h, i) => (
                                    <tr key={`${h.timestamp || ""}-${h.actor_email || ""}-${i}`} className="border-b border-[#e4e4e7] last:border-0">
                                        <td className="px-4 py-2 mono text-[11px] text-[#71717a]">
                                            {new Date(h.timestamp).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2 mono text-[11px]">{h.actor_email || "—"}</td>
                                        <td className="px-4 py-2 mono text-[11px]">{h.cadence || "—"}</td>
                                        <td className="px-4 py-2 mono text-[11px] font-bold">{h.sent || 0}</td>
                                        <td className="px-4 py-2 mono text-[11px]" style={{ color: h.failed ? "#ff3333" : "#52525b" }}>
                                            {h.failed || 0}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(o) => { if (!o) setConfirm(null); }}
                title={confirm?.kind === "broadcast" ? `Send digest to ${confirm.count} recipients?` : "Confirm"}
                description="The internal team digest will be delivered via Resend. Provider directories and customers receive nothing."
                confirmLabel="Send now"
                onConfirm={async () => {
                    if (confirm?.run) await confirm.run();
                    setConfirm(null);
                }}
            />
        </section>
    );
}

function StatusTile({ label, value, ok, mono, testId }) {
    return (
        <div className="border border-[#e4e4e7] p-3 flex flex-col gap-1" data-testid={testId}>
            <span className="label-kicker flex items-center gap-1" style={{ fontSize: 9 }}>
                {ok ? <CheckCircle size={11} weight="bold" color="#002FA7" /> : <XCircle size={11} weight="bold" color="#ff3333" />}
                {label}
            </span>
            <span className={(mono ? "mono " : "font-display ") + "text-[14px] font-bold break-all"}>
                {value}
            </span>
        </div>
    );
}

// ==================== Ops Tab ====================
function OpsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/ops/overview`);
            setData(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const triggerJanitor = async () => {
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/janitor/run`);
            toast.success(`Janitor swept: ${JSON.stringify(data)}`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Janitor failed");
        } finally {
            setBusy(false);
        }
    };

    const flushCache = async () => {
        setBusy(true);
        try {
            const { data } = await axios.delete(`${API}/cache`);
            toast.success(`Cache flushed: ${data.deleted} rows`);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Flush failed");
        } finally {
            setBusy(false);
        }
    };

    if (loading || !data) {
        return <div className="panel p-10 text-center text-sm text-[#52525b]">Loading…</div>;
    }

    return (
        <section className="space-y-6" data-testid="admin-ops-tab">
            <div className="panel">
                <div className="panel-header flex items-center justify-between">
                    <span className="label-kicker flex items-center gap-2">
                        <Database size={12} weight="bold" /> Operations snapshot
                    </span>
                    <button onClick={reload} className="btn-ghost mono text-[11px]" data-testid="admin-ops-refresh">
                        <ArrowsClockwise size={12} weight="bold" /> Refresh
                    </button>
                </div>
                <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Tile label="In-flight runs" value={data.runs.in_flight} testId="ops-in-flight" />
                    <Tile label="Interrupted" value={data.runs.interrupted_total} testId="ops-interrupted" warn={data.runs.interrupted_total > 0} />
                    <Tile label="Failures (24h)" value={data.runs.failed_24h} testId="ops-failed-24h" warn={data.runs.failed_24h > 0} />
                    <Tile label="Cache rows" value={data.cache.total} testId="ops-cache-total" />
                </div>
                <div className="px-5 pb-5 flex flex-wrap gap-2">
                    <button
                        onClick={() => setConfirm({ kind: "janitor", run: triggerJanitor })}
                        disabled={busy}
                        className="btn-ghost mono text-[11px]"
                        data-testid="ops-trigger-janitor"
                    >
                        <Broom size={12} weight="bold" /> Run janitor sweep
                    </button>
                    <button
                        onClick={() => setConfirm({ kind: "flush", count: data.cache.total, run: flushCache })}
                        disabled={busy}
                        className="btn-ghost mono text-[11px]"
                        data-testid="ops-flush-cache"
                    >
                        <Trash size={12} weight="bold" /> Flush cache ({data.cache.total})
                    </button>
                </div>
            </div>

            <div className="panel" data-testid="ops-scheduler">
                <div className="panel-header flex items-center justify-between">
                    <span className="label-kicker flex items-center gap-2">
                        <Clock size={12} weight="bold" /> Scheduler ({data.scheduler.timezone})
                    </span>
                    <span className="mono text-[11px]" style={{ color: data.scheduler.running ? "#002FA7" : "#ff3333" }}>
                        {data.scheduler.running ? "Running" : "Down"}
                    </span>
                </div>
                <div className="divide-y divide-[#e4e4e7]">
                    {data.scheduler.jobs.length === 0 && (
                        <div className="p-4 mono text-[11px] text-[#71717a]">No jobs registered.</div>
                    )}
                    {data.scheduler.jobs.map((j) => (
                        <div key={j.id} className="p-3 grid grid-cols-1 md:grid-cols-3 gap-2 mono text-[11px]">
                            <span className="font-bold">{j.id}</span>
                            <span className="text-[#52525b]">{j.name}</span>
                            <span className="text-[#52525b]">
                                next: {j.next_run_time ? new Date(j.next_run_time).toLocaleString() : "—"}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="panel" data-testid="ops-recent-runs">
                <div className="panel-header">
                    <span className="label-kicker">Recent runs (last 20)</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-[#fafaf9] border-b border-[#e4e4e7] text-left">
                                <th className="px-4 py-2 label-kicker">Created</th>
                                <th className="px-4 py-2 label-kicker">Search</th>
                                <th className="px-4 py-2 label-kicker">Status</th>
                                <th className="px-4 py-2 label-kicker">Results</th>
                                <th className="px-4 py-2 label-kicker">Trigger</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.runs.recent.map((r) => (
                                <tr key={r.id} className="border-b border-[#e4e4e7] last:border-0">
                                    <td className="px-4 py-2 mono text-[11px] text-[#71717a]">
                                        {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                                    </td>
                                    <td className="px-4 py-2 mono text-[11px]">{r.category} · {r.location}</td>
                                    <td className="px-4 py-2 mono text-[11px]">
                                        <span className="px-1.5 py-0.5 border border-[#e4e4e7]" style={{ color: statusColor(r.status) }}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 mono text-[11px]">{r.result_count ?? 0}</td>
                                    <td className="px-4 py-2 mono text-[11px] text-[#71717a]">{r.triggered_by || "manual"}</td>
                                </tr>
                            ))}
                            {data.runs.recent.length === 0 && (
                                <tr><td colSpan={5} className="px-4 py-10 text-center text-[#71717a] text-sm">No runs yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(o) => { if (!o) setConfirm(null); }}
                title={confirm?.kind === "flush"
                    ? `Flush ${confirm.count} cache rows?`
                    : "Run janitor sweep now?"}
                description={confirm?.kind === "flush"
                    ? "All cached website extractions will be discarded. The next crawl will re-fetch every domain — this can be slow."
                    : "Marks stale runs as interrupted, expires aged sessions, and trims aged cache rows."}
                confirmLabel={confirm?.kind === "flush" ? "Flush cache" : "Run sweep"}
                destructive={confirm?.kind === "flush"}
                onConfirm={async () => {
                    if (confirm?.run) await confirm.run();
                    setConfirm(null);
                }}
            />
        </section>
    );
}

function Tile({ label, value, testId, warn }) {
    return (
        <div
            className="border p-3 flex flex-col gap-1"
            style={{ borderColor: warn ? "#ff3333" : "#e4e4e7" }}
            data-testid={testId}
        >
            <span className="label-kicker" style={{ fontSize: 9 }}>{label}</span>
            <span className="font-display text-3xl font-bold" style={{ color: warn ? "#ff3333" : "#000" }}>
                {value ?? 0}
            </span>
        </div>
    );
}

function statusColor(s) {
    if (s === "complete") return "#002FA7";
    if (s === "interrupted" || s === "failed") return "#ff3333";
    if (s === "running" || s === "queued") return "#b07000";
    return "#52525b";
}

// ==================== Confirm Dialog ====================
function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, onConfirm, destructive }) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent data-testid="admin-confirm-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription>{description}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="admin-confirm-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onConfirm}
                        data-testid="admin-confirm-action"
                        style={destructive ? { background: "#ff3333", color: "#fff" } : undefined}
                    >
                        {confirmLabel || "Confirm"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
